import { memo, useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  categoryRankApi,
  type CategoryRankGroup,
  type CategoryRankParams,
  type CategoryRankViewType,
} from '../../api/categoryRank.js';
import { ErrorBox, Field, Spinner } from '../../components/ui.js';

/**
 * 카테고리 랭킹 순위 — 원본 xadmin/management/clarity/list.php.
 *
 * 원본 화면이 하던 일
 *  - 월별/주차별 보기. 보기를 바꾸면 기준일자를 비우고 바로 다시 조회한다
 *    (대분류·정렬은 그대로).
 *  - 기준일자(월별은 달, 주차별은 날짜)와 대분류는 고르는 즉시 조회한다.
 *  - Total 머리칸을 누르면 조회수 정렬 방향만 뒤집는다.
 *  - 쪽 나눔 없이 전부를 기간별로 묶고, 순위는 기간마다 1부터 센다.
 *    묶음마다 그 기간의 조회수 합계를 낸다.
 *
 * 조건은 URL 이 갖는다 - 원본처럼 주소만 복사하면 같은 화면이 열린다.
 */

const VIEW_LABEL: Record<CategoryRankViewType, string> = { month: '월별', week: '주차별' };

function paramsFrom(sp: URLSearchParams): CategoryRankParams {
  return {
    view_type: sp.get('view_type') === 'week' ? 'week' : 'month',
    search_date: sp.get('search_date') ?? '',
    search_category: sp.get('search_category') ?? '',
    sort_dir: (sp.get('sort_dir') ?? '').toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
  };
}

/** 원본 formatNumber - 값이 없으면 0. */
const fmt = (n: number | null | undefined) => (n ? n.toLocaleString() : '0');

/**
 * 한 번에 그리는 행 수. 주차별 전체는 6천 행이 넘어 통째로 그리면 화면이 한참
 * 멈춘다 - 앞쪽부터 이만큼씩 그리고 나머지는 이어서 채운다. 다 그린 뒤의 화면은
 * 원본처럼 전부다.
 */
const RENDER_CHUNK = 500;

/** 새 결과가 오면 RENDER_CHUNK 부터 다시 센다. 결과가 바뀐 그 렌더에서 바로 줄어야 한꺼번에 그리는 일이 없다. */
function useRenderLimit(data: unknown, total: number): number {
  const [shown, setShown] = useState<{ data: unknown; limit: number }>({ data: null, limit: RENDER_CHUNK });
  const limit = shown.data === data ? shown.limit : RENDER_CHUNK;
  useEffect(() => {
    if (limit >= total) return;
    const id = setTimeout(() => setShown({ data, limit: limit + RENDER_CHUNK }), 0);
    return () => clearTimeout(id);
  }, [data, limit, total]);
  return limit;
}

/** 기간 하나. 그릴 행 수가 그대로인 기간은 다시 그리지 않는다 (memo). */
const PeriodRows = memo(function PeriodRows({
  group,
  count,
  names,
  viewLabel,
}: {
  group: CategoryRankGroup;
  count: number;
  names: Record<string, string>;
  viewLabel: string;
}) {
  return (
    <>
      <tr className="rank-period">
        <td colSpan={6}>
          <div className="rank-period__head">
            <div>
              <div className="rank-period__name">{group.period}</div>
              <div className="rank-period__meta">
                {viewLabel} · {fmt(group.rows.length)}개 카테고리
              </div>
            </div>
            <div className="rank-period__totals">
              <span>
                기간 합계 <strong>{fmt(group.total)}</strong>
              </span>
            </div>
          </div>
        </td>
      </tr>
      {group.rows.slice(0, count).map(([category, p, m, total], i) => (
        <tr key={category}>
          <td className="rank-rank">{i + 1}</td>
          <td>
            <span className="rank-period__label">{group.period}</span>
          </td>
          <td>
            {names[category]}
            <span className="rank-code">({category})</span>
          </td>
          <td className="num rank-num--pc">{fmt(p)}</td>
          <td className="num rank-num--mobile">{fmt(m)}</td>
          <td className="num rank-num--total">{fmt(total)}</td>
        </tr>
      ))}
    </>
  );
});

export function CategoryRankPage() {
  const [sp, setSp] = useSearchParams();
  const params = paramsFrom(sp);

  const list = useQuery({
    queryKey: ['category-rank', params],
    queryFn: () => categoryRankApi.list(params),
    placeholderData: keepPreviousData,
  });

  /** 원본 search() - 지금 조건에 바뀐 것만 얹어 다시 연다. */
  function go(patch: Partial<CategoryRankParams>) {
    const next = { ...params, ...patch };
    const out = new URLSearchParams();
    if (next.view_type !== 'month') out.set('view_type', next.view_type);
    if (next.search_date) out.set('search_date', next.search_date);
    if (next.search_category) out.set('search_category', next.search_category);
    if (next.sort_dir !== 'DESC') out.set('sort_dir', next.sort_dir);
    setSp(out);
  }

  const data = list.data;
  const groups = data?.groups ?? [];
  const viewLabel = VIEW_LABEL[params.view_type];
  const limit = useRenderLimit(data, data?.total ?? 0);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            <button type="button" className="rank-title" onClick={() => setSp(new URLSearchParams())}>
              카테고리 랭킹 순위 (클릭순)
            </button>
          </h1>
          <p className="page-desc">카테고리별 클릭(PC · 모바일)을 월별·주차별로 묶어 순위를 매깁니다.</p>
        </div>
        <div className="rank-badge">
          조회된 내역: <strong>{fmt(data?.total)}</strong>건
        </div>
      </header>

      <div className="filters">
        <Field label="보기">
          <select
            className="input"
            value={params.view_type}
            onChange={(e) => go({ view_type: e.target.value as CategoryRankViewType, search_date: '' })}
          >
            <option value="month">월별</option>
            <option value="week">주차별</option>
          </select>
        </Field>
        <Field label="기준일자">
          <input
            key={params.view_type}
            className="input"
            type={params.view_type === 'month' ? 'month' : 'date'}
            value={params.search_date}
            onChange={(e) => go({ search_date: e.target.value })}
          />
        </Field>
        <Field label="대분류">
          <select
            className="input"
            value={params.search_category}
            onChange={(e) => go({ search_category: e.target.value })}
          >
            <option value="">카테고리 대분류 (전체)</option>
            {data?.categories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        <button type="button" className="btn btn--ghost" onClick={() => setSp(new URLSearchParams())}>
          ↺ 초기화
        </button>
      </div>

      {list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : !data ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <div className="rank-summary">
            <div>
              <div className="rank-summary__title">
                <span>{viewLabel}</span> 기간 그룹
              </div>
              <div className="rank-summary__sub">조회 결과 {fmt(data.total)}건</div>
            </div>
            <div className="rank-summary__pill">기간 {fmt(groups.length)}개</div>
          </div>
          <table className="table rank-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'center', width: 70 }}>순위</th>
                <th style={{ width: '15%' }}>조회 기간</th>
                <th>카테고리</th>
                <th style={{ textAlign: 'right', width: '15%' }}>PC</th>
                <th style={{ textAlign: 'right', width: '15%' }}>Mobile</th>
                <th
                  style={{ textAlign: 'right', width: '15%' }}
                  aria-sort={params.sort_dir === 'ASC' ? 'ascending' : 'descending'}
                >
                  <button
                    type="button"
                    className="th-sort"
                    title="클릭하여 정렬 변경"
                    onClick={() => go({ sort_dir: params.sort_dir === 'DESC' ? 'ASC' : 'DESC' })}
                  >
                    Total{' '}
                    <span className="th-sort__arrow th-sort__arrow--on">{params.sort_dir === 'DESC' ? '▼' : '▲'}</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, i) => {
                // 앞 기간들이 RENDER_CHUNK 를 다 썼으면 이 기간은 아직 안 그린다.
                const before = groups.slice(0, i).reduce((n, x) => n + x.rows.length, 0);
                if (before >= limit) return null;
                return (
                  <PeriodRows
                    key={g.periodKey}
                    group={g}
                    count={Math.min(g.rows.length, limit - before)}
                    names={data.names}
                    viewLabel={viewLabel}
                  />
                );
              })}
              {data.total === 0 && (
                <tr>
                  <td colSpan={6} className="rank-empty">
                    조회된 데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
